/**
 * Shared shape + helpers for the Weekly Meal Planner.
 *
 * A "slot" = one row in `menu_recipes`. A slot is either:
 *   - assigned to a day + meal, holding a recipe OR a free-text note
 *   - unscheduled (`day_index = null`), shown in the Unscheduled tray
 */

export type MealSlot = "breakfast" | "lunch" | "dinner" | "snacks";

export const DAYS: { index: number; short: string; long: string }[] = [
  { index: 0, short: "Mon", long: "Monday" },
  { index: 1, short: "Tue", long: "Tuesday" },
  { index: 2, short: "Wed", long: "Wednesday" },
  { index: 3, short: "Thu", long: "Thursday" },
  { index: 4, short: "Fri", long: "Friday" },
  { index: 5, short: "Sat", long: "Saturday" },
  { index: 6, short: "Sun", long: "Sunday" },
];

export const MEAL_SLOTS: { id: MealSlot; label: string }[] = [
  { id: "breakfast", label: "Breakfast" },
  { id: "lunch", label: "Lunch" },
  { id: "dinner", label: "Dinner" },
  { id: "snacks", label: "Snacks" },
];

/** Preset chips offered alongside the free-text "Quick note" input. */
export const NOTE_PRESETS = [
  "Eating out",
  "Leftovers",
  "Takeaway",
  "Brunch out",
  "Fasting",
];

/** Default visible meals for a fresh plan when the user has no global default. */
export const DEFAULT_VISIBLE_MEALS: MealSlot[] = ["breakfast", "lunch", "dinner"];

const ALL_MEAL_IDS: MealSlot[] = ["breakfast", "lunch", "dinner", "snacks"];

/**
 * Coerce arbitrary jsonb / unknown into a clean, ordered MealSlot[]:
 *  - filters invalid entries
 *  - dedupes
 *  - preserves the canonical MEAL_SLOTS ordering (breakfast → snacks)
 */
export const sanitiseVisibleMeals = (raw: unknown): MealSlot[] => {
  if (!Array.isArray(raw)) return [...DEFAULT_VISIBLE_MEALS];
  const set = new Set<MealSlot>();
  for (const v of raw) {
    if (typeof v === "string" && (ALL_MEAL_IDS as string[]).includes(v)) {
      set.add(v as MealSlot);
    }
  }
  if (set.size === 0) return [...DEFAULT_VISIBLE_MEALS];
  return ALL_MEAL_IDS.filter((id) => set.has(id));
};

export type PlannerSlot = {
  id: string;
  menu_id: string;
  recipe_id: string | null;
  servings_override: number | null;
  day_index: number | null;
  meal_slot: MealSlot | null;
  note: string | null;
  position: number;
  recipe?: {
    id: string;
    share_id: string;
    title: string;
    image_url: string | null;
    tags: unknown;
    ingredients: unknown;
    servings: string | null;
  } | null;
};

/** Build a quick lookup so cells can ask "what's in (day=2, slot='dinner')?" */
export const indexSlots = (
  slots: PlannerSlot[],
): {
  byCell: Map<string, PlannerSlot[]>;
  unscheduled: PlannerSlot[];
} => {
  const byCell = new Map<string, PlannerSlot[]>();
  const unscheduled: PlannerSlot[] = [];
  for (const s of slots) {
    if (s.day_index == null || s.meal_slot == null) {
      unscheduled.push(s);
      continue;
    }
    const key = cellKey(s.day_index, s.meal_slot);
    const list = byCell.get(key);
    if (list) list.push(s);
    else byCell.set(key, [s]);
  }
  return { byCell, unscheduled };
};

export const cellKey = (day: number, slot: MealSlot) => `${day}:${slot}`;

/**
 * Reorder DAYS so the week starts on `startDay` (0=Mon … 6=Sun).
 * The underlying day indices (used as foreign keys in `menu_recipes.day_index`)
 * never change — we just rotate the display order.
 */
export const orderedDays = (startDay: number) => {
  const safe = Number.isFinite(startDay) ? ((startDay % 7) + 7) % 7 : 0;
  return [...DAYS.slice(safe), ...DAYS.slice(0, safe)];
};
